import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GoogleMap, Polygon, Marker, useJsApiLoader } from '@react-google-maps/api';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Map as MapIcon,
  Save as SaveIcon,
  Undo as UndoIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';
import {
  createSector,
  deleteSector,
  fetchSectors,
  updateSector
} from '../../redux/slices/sectorsSlice';
import {
  fetchSettings,
  saveSettingImmediately
} from '../../redux/slices/settingsSlice';
import { useTransportWording } from '../../utils/transportWording';
import SectorImportDialog from './SectorImportDialog';
import {
  calculateBounds,
  calculateCentroid,
  hasSelfIntersection
} from '../../utils/sectorGeometry';
import useMapColorScheme from '../../hooks/useMapColorScheme';

const DEFAULT_CENTER = { lat: 53.5511, lng: 9.9937 };
const DEFAULT_COLOR = '#2E7D32';
const SERVICE_AREA_COLOR = '#1565C0';
const MAP_CONTAINER_STYLE = { width: '100%', height: '620px' };

const emptyForm = {
  id: null,
  name: '',
  code: '',
  description: '',
  color: DEFAULT_COLOR,
  active: true,
  dispatchFromQueueEnd: false,
  sortOrder: 0,
  priority: 100,
  polygon: []
};

const SectorManagementPage = () => {
  const wording = useTransportWording();
  const dispatch = useDispatch();
  const polygonRef = useRef(null);
  const { sectors, isLoading, isSaving, error } = useSelector(state => state.sectors);
  const globalSettings = useSelector(state => state.settings.global);
  const settingsSaving = useSelector(state => state.settings.isSaving);

  const [form, setForm] = useState(emptyForm);
  const [editorMode, setEditorMode] = useState('sector');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serviceAreaDeleteDialogOpen, setServiceAreaDeleteDialogOpen] = useState(false);
  const [sectorToDelete, setSectorToDelete] = useState(null);
  const mapScheme = useMapColorScheme();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [serviceAreaTakeoverOpen, setServiceAreaTakeoverOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importDialogMode, setImportDialogMode] = useState('sectors');
  const initialServiceAreaSwitchRef = useRef(false);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: window.GOOGLE_MAPS_LIBRARIES || ['geometry']
  });

  useEffect(() => {
    dispatch(fetchSectors());
    dispatch(fetchSettings());
  }, [dispatch]);

  const serviceAreaPolygon = useMemo(() => {
    const polygons = Array.isArray(globalSettings.serviceAreaPolygons)
      ? globalSettings.serviceAreaPolygons
      : [];
    const activePolygon = polygons.find(polygon => polygon?.active !== false) || polygons[0];
    const points = activePolygon?.points || activePolygon?.polygon || [];
    return Array.isArray(points) ? points : [];
  }, [globalSettings.serviceAreaPolygons]);

  const isServiceAreaPolygonMode =
    globalSettings.serviceAreaEnabled === true && globalSettings.serviceAreaMode === 'polygon';
  const hasServiceAreaPolygon = serviceAreaPolygon.length >= 3;
  const mustDefineServiceArea = isServiceAreaPolygonMode && !hasServiceAreaPolygon;
  const isServiceAreaEditor = editorMode === 'serviceArea';
  const editorColor = isServiceAreaEditor ? SERVICE_AREA_COLOR : (form.color || DEFAULT_COLOR);

  const activeSectorCount = useMemo(
    () => sectors.filter(sector => sector.active !== false).length,
    [sectors]
  );

  const mapCenter = useMemo(() => {
    if (form.polygon.length) return calculateCentroid(form.polygon);
    if (serviceAreaPolygon.length) return calculateCentroid(serviceAreaPolygon);
    const firstSectorWithCentroid = sectors.find(sector => sector.centroid);
    return firstSectorWithCentroid?.centroid || DEFAULT_CENTER;
  }, [form.polygon, sectors, serviceAreaPolygon]);

  const validationError = useMemo(() => {
    if (editorMode === 'serviceArea') {
      if (form.polygon.length < 3) return 'Mindestens 3 Polygonpunkte erforderlich';
      if (hasSelfIntersection(form.polygon)) return 'Polygon darf sich nicht selbst schneiden';
      return null;
    }
    if (!form.id && mustDefineServiceArea) return 'Bitte zuerst das Betriebsgebiet festlegen';
    if (!form.name.trim()) return 'Name ist erforderlich';
    if (!form.code.trim()) return 'Sektornummer ist erforderlich';
    if (form.polygon.length < 3) return 'Mindestens 3 Polygonpunkte erforderlich';
    if (hasSelfIntersection(form.polygon)) return 'Polygon darf sich nicht selbst schneiden';
    return null;
  }, [form, editorMode, mustDefineServiceArea]);

  const handleMapClick = (event) => {
    if (!event.latLng) return;
    setForm(prev => ({
      ...prev,
      polygon: [
        ...prev.polygon,
        {
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        }
      ]
    }));
  };

  const syncPolygonFromMap = () => {
    if (!polygonRef.current) return;
    const path = polygonRef.current.getPath();
    const nextPolygon = [];
    for (let i = 0; i < path.getLength(); i += 1) {
      const point = path.getAt(i);
      nextPolygon.push({ lat: point.lat(), lng: point.lng() });
    }
    setForm(prev => ({ ...prev, polygon: nextPolygon }));
  };

  const handleEdit = (sector) => {
    setEditorMode('sector');
    setForm({
      id: sector.id,
      name: sector.name || '',
      code: sector.code || '',
      description: sector.description || '',
      color: sector.color || DEFAULT_COLOR,
      active: sector.active !== false,
      dispatchFromQueueEnd: sector.dispatchFromQueueEnd === true,
      sortOrder: sector.sortOrder || 0,
      priority: sector.priority || 100,
      polygon: Array.isArray(sector.polygon) ? sector.polygon : []
    });
  };

  const handleNew = () => {
    polygonRef.current = null;
    setEditorMode(mustDefineServiceArea ? 'serviceArea' : 'sector');
    setForm(emptyForm);
  };

  const openServiceAreaEditor = (polygon) => {
    polygonRef.current = null;
    setEditorMode('serviceArea');
    setForm({
      ...emptyForm,
      name: 'Betriebsgebiet',
      code: 'BG',
      color: SERVICE_AREA_COLOR,
      polygon
    });
  };

  const handleEditServiceArea = () => {
    // Ein bereits gezeichnetes, noch nicht gespeichertes Sektor-Polygon darf
    // beim Wechsel nicht stillschweigend verloren gehen — meistens ist genau
    // diese Flaeche das gesuchte Betriebsgebiet.
    if (!isServiceAreaEditor && !form.id && form.polygon.length >= 3) {
      setServiceAreaTakeoverOpen(true);
      return;
    }
    openServiceAreaEditor(serviceAreaPolygon);
  };

  const handleServiceAreaTakeover = () => {
    const drawnPolygon = form.polygon;
    setServiceAreaTakeoverOpen(false);
    openServiceAreaEditor(drawnPolygon);
  };

  const handleServiceAreaTakeoverDiscard = () => {
    setServiceAreaTakeoverOpen(false);
    openServiceAreaEditor(serviceAreaPolygon);
  };

  // Beim Betreten der Seite entscheidet allein, ob ein Betriebsgebiet existiert:
  // fehlt es, startet der Editor in Schritt 1, sonst im Sektor-Modus. Nur einmal
  // pro Seitenaufruf, damit die Bearbeitung eines bestehenden Sektors danach
  // nicht wieder weggeschaltet wird.
  useEffect(() => {
    if (initialServiceAreaSwitchRef.current) return;
    if (!mustDefineServiceArea || form.id) return;
    initialServiceAreaSwitchRef.current = true;
    handleEditServiceArea();
  }, [mustDefineServiceArea, form.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const openImportDialog = (mode) => {
    setImportDialogMode(mode);
    setImportDialogOpen(true);
  };

  const handleUndoPoint = () => {
    setForm(prev => ({ ...prev, polygon: prev.polygon.slice(0, -1) }));
  };

  const handleClearPolygon = () => {
    polygonRef.current = null;
    setForm(prev => ({ ...prev, polygon: [] }));
  };

  const handleSave = async () => {
    if (validationError) {
      setSnackbar({ open: true, message: validationError, severity: 'warning' });
      return;
    }

    if (editorMode === 'serviceArea') {
      const serviceArea = {
        id: 'main',
        name: 'Betriebsgebiet',
        active: true,
        points: form.polygon,
        bounds: calculateBounds(form.polygon)
      };

      const result = await dispatch(saveSettingImmediately({
        key: 'serviceAreaPolygons',
        value: [serviceArea]
      }));

      if (result.error) {
        setSnackbar({ open: true, message: result.payload || result.error.message, severity: 'error' });
        return;
      }

      setSnackbar({ open: true, message: 'Betriebsgebiet wurde gespeichert', severity: 'success' });
      await dispatch(fetchSettings());
      polygonRef.current = null;
      setEditorMode('sector');
      setForm(emptyForm);
      return;
    }

    const payload = {
      name: form.name,
      code: form.code,
      description: form.description,
      color: form.color || DEFAULT_COLOR,
      active: form.active,
      dispatchFromQueueEnd: form.dispatchFromQueueEnd,
      sortOrder: Number(form.sortOrder) || 0,
      priority: Number(form.priority) || 100,
      polygon: form.polygon,
      bounds: calculateBounds(form.polygon),
      centroid: calculateCentroid(form.polygon)
    };

    const result = form.id
      ? await dispatch(updateSector({ id: form.id, ...payload }))
      : await dispatch(createSector(payload));

    if (result.error) {
      setSnackbar({ open: true, message: result.payload || result.error.message, severity: 'error' });
      return;
    }

    setSnackbar({ open: true, message: 'Sektor wurde gespeichert', severity: 'success' });
    dispatch(fetchSectors());
    handleNew();
  };

  const handleDeleteServiceArea = async () => {
    const result = await dispatch(saveSettingImmediately({
      key: 'serviceAreaPolygons',
      value: []
    }));

    if (result.error) {
      setSnackbar({ open: true, message: result.payload || result.error.message, severity: 'error' });
      return;
    }

    setSnackbar({ open: true, message: 'Betriebsgebiet wurde entfernt', severity: 'success' });
    setServiceAreaDeleteDialogOpen(false);
    await dispatch(fetchSettings());
    polygonRef.current = null;
    setEditorMode('serviceArea');
    setForm({
      ...emptyForm,
      name: 'Betriebsgebiet',
      code: 'BG',
      color: SERVICE_AREA_COLOR,
      polygon: []
    });
  };

  const handleDeleteClick = (sector) => {
    setSectorToDelete(sector);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!sectorToDelete) return;
    const result = await dispatch(deleteSector(sectorToDelete.id));
    if (result.error) {
      setSnackbar({ open: true, message: result.payload || result.error.message, severity: 'error' });
    } else {
      setSnackbar({ open: true, message: 'Sektor wurde gelöscht', severity: 'success' });
      if (form.id === sectorToDelete.id) handleNew();
    }
    setDeleteDialogOpen(false);
    setSectorToDelete(null);
  };

  if (isLoading) {
    return <Loading message="Sektoren werden geladen..." />;
  }

  if (error && sectors.length === 0) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Sektoren"
        message={error}
        onRetry={() => dispatch(fetchSectors())}
      />
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title="Sektoren verwalten"
        description="Fahrgebiet als präzise Polygon-Sektoren zeichnen und verwalten"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Einstellungen', path: '/einstellungen' },
          { label: 'Sektoren', path: '/einstellungen/sektoren' }
        ]}
      />

      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>Logik der Sektor-Disposition</AlertTitle>
        <Typography variant="body2">
          Fahrer werden immer genau einem aktiven Sektor zugeordnet. Wenn sich gezeichnete Sektoren überlappen,
          gewinnt der Sektor mit der höheren Priorität; bei gleicher Priorität entscheidet die niedrigere Sortierung.
        </Typography>
        <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
          <Typography component="li" variant="body2">
            Der Gewinner-Sektor ist in der Fahrer-App grün und bestimmt die Warteschlange.
          </Typography>
          <Typography component="li" variant="body2">
            Weitere überlappende Sektoren werden in der Fahrer-App gelb angezeigt, ändern aber nicht die Anmeldung.
          </Typography>
          <Typography component="li" variant="body2">
            Nur aktive Sektoren werden für Fahrer-App, Warteschlange und automatische Vermittlung berücksichtigt.
          </Typography>
        </Box>
      </Alert>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h6">Karteneditor</Typography>
                <Typography variant="body2" color="text.secondary">
                  Auf die Karte klicken, um Polygonpunkte zu setzen. Gespeicherte Polygone sind halbtransparent sichtbar.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Einzelne Punkte können nachträglich verschoben werden; das gesamte Polygon kann bewusst nicht als Fläche verschoben werden.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isServiceAreaEditor
                    ? 'Das feste Betriebsgebiet benötigt mindestens 3 Punkte. Es begrenzt lokale Vorbestellungen.'
                    : 'Ein Sektor benötigt mindestens 3 Punkte. Selbstüberschneidungen werden beim Speichern verhindert.'}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Letzten Punkt entfernen">
                  <span>
                    <IconButton onClick={handleUndoPoint} disabled={!form.polygon.length}>
                      <UndoIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Polygon leeren">
                  <span>
                    <IconButton onClick={handleClearPolygon} disabled={!form.polygon.length}>
                      <ClearIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>

            {loadError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Google Maps konnte nicht geladen werden.
              </Alert>
            )}

            {!isLoaded ? (
              <Loading message="Karte wird geladen..." />
            ) : (
              <GoogleMap
                mapContainerStyle={MAP_CONTAINER_STYLE}
                center={mapCenter}
                zoom={12}
                onClick={handleMapClick}
                // Ohne `key`-Wechsel: Eine laufende Zeichnung soll beim Umschalten
                // des Nachtmodus nicht verloren gehen; das Schema greift beim nächsten Öffnen.
                options={{
                  colorScheme: mapScheme.colorScheme,
                  streetViewControl: false,
                  mapTypeControl: true,
                  fullscreenControl: true
                }}
              >
                {!isServiceAreaEditor && hasServiceAreaPolygon && (
                  <Polygon
                    key="service-area"
                    paths={serviceAreaPolygon}
                    options={{
                      fillColor: SERVICE_AREA_COLOR,
                      fillOpacity: 0.1,
                      strokeColor: SERVICE_AREA_COLOR,
                      strokeOpacity: 0.9,
                      strokeWeight: 3,
                      clickable: false
                    }}
                  />
                )}

                {sectors.map(sector => (
                  sector.id !== form.id && Array.isArray(sector.polygon) && sector.polygon.length >= 3 ? (
                    <Polygon
                      key={sector.id}
                      paths={sector.polygon}
                      options={{
                        fillColor: sector.color || DEFAULT_COLOR,
                        fillOpacity: sector.active === false ? 0.08 : 0.18,
                        strokeColor: sector.color || DEFAULT_COLOR,
                        strokeOpacity: sector.active === false ? 0.35 : 0.75,
                        strokeWeight: 2,
                        clickable: false
                      }}
                    />
                  ) : null
                ))}

                {form.polygon.length > 0 && (
                  <>
                    <Polygon
                      paths={form.polygon}
                      editable
                      onLoad={(polygon) => {
                        polygonRef.current = polygon;
                      }}
                      onMouseUp={syncPolygonFromMap}
                      options={{
                        fillColor: editorColor,
                        fillOpacity: 0.28,
                        strokeColor: editorColor,
                        strokeOpacity: 0.95,
                        strokeWeight: 3
                      }}
                    />
                    {form.polygon.map((point, index) => (
                      <Marker
                        key={`${point.lat}-${point.lng}-${index}`}
                        position={point}
                        label={`${index + 1}`}
                      />
                    ))}
                  </>
                )}
              </GoogleMap>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MapIcon color="primary" />
                  <Typography variant="h6">
                    {isServiceAreaEditor
                      ? (mustDefineServiceArea ? 'Schritt 1 von 2: Betriebsgebiet' : 'Betriebsgebiet bearbeiten')
                      : (form.id ? 'Sektor bearbeiten' : 'Neuer Sektor')}
                  </Typography>
                </Box>
                {isServiceAreaEditor ? (
                  <Tooltip title="Amtliche Stadt- oder Gemeindegrenze suchen oder aus einer Datenquelle laden">
                    <Button
                      size="small"
                      startIcon={<CloudUploadIcon />}
                      onClick={() => openImportDialog('serviceArea')}
                    >
                      Grenzen importieren
                    </Button>
                  </Tooltip>
                ) : (
                  <Stack direction="row" spacing={1}>
                    <Tooltip
                      title={mustDefineServiceArea
                        ? 'Erst das Betriebsgebiet festlegen'
                        : 'Stadtteilgrenzen suchen oder aus einer Datenquelle laden'}
                    >
                      <span>
                        <Button
                          size="small"
                          startIcon={<CloudUploadIcon />}
                          onClick={() => openImportDialog('sectors')}
                          disabled={mustDefineServiceArea}
                        >
                          Grenzen importieren
                        </Button>
                      </span>
                    </Tooltip>
                    <Button size="small" startIcon={<AddIcon />} onClick={handleNew}>
                      Neuer Sektor
                    </Button>
                  </Stack>
                )}
              </Stack>

              {isServiceAreaEditor ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Zeichnen Sie das Betriebsgebiet als Polygon. Abholungen innerhalb dieses Gebiets werden als lokal
                  behandelt und 15 Minuten vor Abholzeit für die Disposition freigegeben.
                  {mustDefineServiceArea && ' Danach folgt Schritt 2: die Sektoren.'}
                </Alert>
              ) : null}

              {!isServiceAreaEditor && (
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 7 }}>
                    <TextField
                      fullWidth
                      label="Name"
                      value={form.name}
                      onChange={(event) => setForm(prev => ({ ...prev, name: event.target.value }))}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 5 }}>
                    <TextField
                      fullWidth
                      label="Sektornummer"
                      value={form.code}
                      onChange={(event) => setForm(prev => ({ ...prev, code: event.target.value }))}
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      label="Beschreibung"
                      value={form.description}
                      onChange={(event) => setForm(prev => ({ ...prev, description: event.target.value }))}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      label="Farbe"
                      type="color"
                      value={form.color}
                      onChange={(event) => setForm(prev => ({ ...prev, color: event.target.value }))}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      label="Sortierung"
                      type="number"
                      value={form.sortOrder}
                      onChange={(event) => setForm(prev => ({ ...prev, sortOrder: event.target.value }))}
                      helperText="Niedrigerer Wert gewinnt, wenn die Priorität gleich ist."
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      label="Priorität"
                      type="number"
                      value={form.priority}
                      onChange={(event) => setForm(prev => ({ ...prev, priority: event.target.value }))}
                      helperText="Höherer Wert gewinnt bei überlappenden Sektoren."
                    />
                  </Grid>
                  <Grid size={12}>
                    <Stack spacing={1}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={form.active}
                            onChange={(event) => setForm(prev => ({ ...prev, active: event.target.checked }))}
                          />
                        }
                        label="Aktiv"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={form.dispatchFromQueueEnd}
                            onChange={(event) => setForm(prev => ({
                              ...prev,
                              dispatchFromQueueEnd: event.target.checked
                            }))}
                          />
                        }
                        label={`${wording.pick('Letztes Taxi', 'Letzten Wagen')} zuerst disponieren`}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ pl: 6 }}>
                        Für Bahnhof, Flughafen oder {wording.pick('Taxistände', 'Warteplätze')}: Die sichtbare Position bleibt unverändert,
                        die automatische Angebotsreihenfolge beginnt intern hinten.
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              )}

              <Divider sx={{ my: 2 }} />

              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Polygonpunkte: {form.polygon.length}
                </Typography>
                <Chip
                  size="small"
                  label={validationError || 'Speicherbereit'}
                  color={validationError ? 'warning' : 'success'}
                  variant={validationError ? 'outlined' : 'filled'}
                />
              </Stack>

              <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
                {isServiceAreaEditor
                  ? 'Änderungen am Betriebsgebiet wirken auf die dynamische Freigabe von Vorbestellungen.'
                  : 'Änderungen an aktiven Sektoren wirken nach dem Speichern auf Fahrer-App, Warteschlange und Sektor-Disposition. Für produktive Daten ist Deaktivieren meist sicherer als Löschen.'}
              </Alert>

              <Tooltip title={validationError || ''} disableHoverListener={!validationError}>
                <span>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={Boolean(validationError) || isSaving || settingsSaving}
                  >
                    {isServiceAreaEditor ? 'Betriebsgebiet speichern' : (form.id ? 'Sektor speichern' : 'Sektor anlegen')}
                  </Button>
                </span>
              </Tooltip>
            </CardContent>
          </Card>
        </Grid>

        {isServiceAreaPolygonMode && (
          <Grid size={12}>
            <Paper sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h6">Betriebsgebiet</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Dieses Polygon entscheidet, ob eine Vorbestellung lokal ist und fest 15 Minuten vor Abholzeit freigegeben wird.
                  </Typography>
                </Box>
                <Chip
                  label={hasServiceAreaPolygon ? 'Polygon festgelegt' : 'Fehlt'}
                  color={hasServiceAreaPolygon ? 'success' : 'warning'}
                  size="small"
                />
              </Stack>

              {hasServiceAreaPolygon ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Typ</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell align="center">Punkte</TableCell>
                        <TableCell align="right">Aktionen</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow selected={isServiceAreaEditor}>
                        <TableCell>
                          <Chip
                            label="BG"
                            size="small"
                            sx={{ backgroundColor: SERVICE_AREA_COLOR, color: 'white' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">Betriebsgebiet</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Aktiv für dynamische Vorbestellungs-Disposition
                          </Typography>
                        </TableCell>
                        <TableCell align="center">{serviceAreaPolygon.length}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Betriebsgebiet bearbeiten">
                            <IconButton size="small" onClick={handleEditServiceArea}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Betriebsgebiet entfernen">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setServiceAreaDeleteDialogOpen(true)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert
                  severity="warning"
                  action={
                    <Button color="inherit" size="small" onClick={handleEditServiceArea}>
                      Jetzt festlegen
                    </Button>
                  }
                >
                  Im Modus "Festes Gebiet (Polygon)" fehlt noch das Betriebsgebiet. Neue Sektoren können erst angelegt werden,
                  nachdem dieses Gebiet gespeichert wurde.
                </Alert>
              )}
            </Paper>
          </Grid>
        )}

        <Grid size={12}>
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Sektoren</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label={`${activeSectorCount}/${sectors.length} aktiv`} color="primary" size="small" />
                <Tooltip
                  title={mustDefineServiceArea ? 'Erst das Betriebsgebiet festlegen' : 'Stadtteilgrenzen suchen oder aus einer Datenquelle laden'}
                >
                  <span>
                    <Button
                      size="small"
                      startIcon={<CloudUploadIcon />}
                      onClick={() => openImportDialog('sectors')}
                      disabled={mustDefineServiceArea}
                    >
                      Grenzen importieren
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nr.</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell align="center">Punkte</TableCell>
                    <TableCell align="right">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sectors.map(sector => (
                    <TableRow key={sector.id} selected={sector.id === form.id}>
                      <TableCell>
                        <Chip
                          label={sector.code || '-'}
                          size="small"
                          sx={{ backgroundColor: sector.color || DEFAULT_COLOR, color: 'white' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{sector.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {sector.active === false ? 'Inaktiv' : 'Aktiv'}
                        </Typography>
                        {sector.dispatchFromQueueEnd === true && (
                          <Chip
                            label={wording.pick('Letztes Taxi zuerst', 'Letzten Wagen zuerst')}
                            size="small"
                            variant="outlined"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">{sector.polygon?.length || 0}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleEdit(sector)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(sector)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {sectors.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          Noch keine Sektoren angelegt.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Aktivierte Sektoren werden von der Fahrer-App geladen. Inaktive Sektoren bleiben zur Dokumentation erhalten,
              werden aber nicht für Anmeldung, Warteschlange oder automatische Vermittlung verwendet.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="Sektor löschen?"
        content={`Soll der Sektor "${sectorToDelete?.name || ''}" wirklich gelöscht werden? Für produktive Daten ist Deaktivieren meist sicherer als Löschen.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialogOpen(false)}
      />

      <DeleteConfirmDialog
        open={serviceAreaDeleteDialogOpen}
        title="Betriebsgebiet entfernen?"
        content={'Soll das Betriebsgebiet wirklich entfernt werden? Im Modus "Festes Gebiet (Polygon)" können danach keine neuen Sektoren angelegt werden, bis ein neues Betriebsgebiet gespeichert wurde.'}
        onConfirm={handleDeleteServiceArea}
        onCancel={() => setServiceAreaDeleteDialogOpen(false)}
      />

      <SectorImportDialog
        open={importDialogOpen}
        mode={importDialogMode}
        onClose={() => setImportDialogOpen(false)}
        existingSectors={sectors}
        isMapLoaded={isLoaded}
        onSelectPolygon={(points) => {
          // Die Flaeche landet im Editor, gespeichert wird sie wie eine von
          // Hand gezeichnete — inklusive Nacharbeit an einzelnen Punkten.
          openServiceAreaEditor(points);
          setSnackbar({
            open: true,
            message: 'Fläche übernommen — jetzt prüfen und „Betriebsgebiet speichern"',
            severity: 'info'
          });
        }}
        onImported={(created) => {
          dispatch(fetchSectors());
          if (created > 0) {
            setSnackbar({
              open: true,
              message: created === 1 ? 'Ein Sektor wurde importiert' : `${created} Sektoren wurden importiert`,
              severity: 'success'
            });
          }
        }}
      />

      <Dialog open={serviceAreaTakeoverOpen} onClose={() => setServiceAreaTakeoverOpen(false)}>
        <DialogTitle>Gezeichnetes Polygon übernehmen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sie haben bereits ein Polygon mit {form.polygon.length} Punkten gezeichnet. Soll diese Fläche als
            Betriebsgebiet übernommen werden? Andernfalls wird sie verworfen und Sie zeichnen das Betriebsgebiet neu.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setServiceAreaTakeoverOpen(false)}>Abbrechen</Button>
          <Button color="inherit" onClick={handleServiceAreaTakeoverDiscard}>Neu zeichnen</Button>
          <Button variant="contained" onClick={handleServiceAreaTakeover}>Übernehmen</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SectorManagementPage;
